import { useState, useEffect } from 'react';
import FormField from '../../../components/FormField/FormField';
import ButtonLabel from '../../../components/Button/ButtonLabel';
import Button from '../../../components/Button/Button';
import { createTokenRecoveryPassword } from '../../../services/tokenValidation';
import { useNavigate } from 'react-router-dom';
import { useControllerFlowStepsContext } from '../../../hooks/ControllerFlowStepsContext';
import { useAccountRecoveryContext } from '../../../data/AccountRecovery/AccountRecoveryContext';
import { checkEmailExist } from '../../../services/queryController';
import { ErrorMessages } from '../../../data/ErrorMessages';
import StepHeader from '../../../components/Wizard/StepHeader';
import Alert from '../../../components/Alert';
import { getEmailProvider } from '../../../utils/string';
import { SocialEnum } from '../../../enums/SocialEnum';
import { pushGtmEvent } from '../../../utils/GenericFunctions';

export interface PasswordRecoveryEmailProps {
  previousPath?: number | null;
  nextPath?: number | null;
  handleChangeStep(e: number): void;
  context: any;
}

export default function PasswordRecoveryEmail({
  nextPath,
  handleChangeStep
}: PasswordRecoveryEmailProps) {
  const [fieldValidate, setValueValidateField] = useState('');
  const {
    register,
    watch,
    errors,
    setError,
    getValues,
    setValue,
    touchedFields
  } = useAccountRecoveryContext();

  const [loading, setLoading] = useState(false);
  const { showErrorMessageServer, setControllerViews } =
    useControllerFlowStepsContext();

  useEffect(() => {
    const email: any = getValues('email');
    setValue(email, { shouldTouch: true });
    setValueValidateField(email);
  }, []);

  useEffect(() => {
    const subscription = watch((data: any) => {
      setValueValidateField(data.email);
    });
    return () => subscription?.unsubscribe();
  }, [watch]);

  const { getOrigin } = useControllerFlowStepsContext();

  const url = `${
    !getOrigin()
      ? '/recuperar-senha/email'
      : '/' + getOrigin() + '/recuperar-senha/email'
  }`;
  const navigate = useNavigate();

  useEffect(() => {
    navigate(url);
  }, [navigate]);

  function nextStep() {
    handleChangeStep(nextPath ?? 1);
  }

  function backPage() {
    navigate(
      `${getOrigin() == 'videos' ? '/videos/' : getOrigin() === 'sbt' ? '/sbt/' : '/'}signin`
    );
  }

  window.onpopstate = () => {
    backPage();
  };

  async function callCreateToken() {
    const formatValue = fieldValidate;
    setLoading(true);

    try {
      await createTokenRecoveryPassword(formatValue);
      setLoading(false);
      nextStep();
    } catch (error: any) {
      setLoading(false);
      setError('email', {
        message:
          error?.statusCode === 429
            ? 'Você atingiu o limite de tentativas. Por favor, volte mais tarde.'
            : 'Não foi possível prosseguir com a alteração de senha para esse e-mail'
      });
    }
  }

  async function emailExist() {
    const email = getValues('email');
    if (email && !errors.email) {
      pushGtmEvent('recovery_email_submitted');
      setLoading(true);
      try {
        const response = await checkEmailExist(
          email,
          ErrorMessages.EMAIL_EXIST_FORGOT_PASSWORD
        );
        if (!response?.exist) {
          setError('email', {
            message: 'e-mail não encontrado no sistema'
          });
          setLoading(false);
        } else {
          const emailProvider = getEmailProvider(email);
          if (emailProvider) {
            pushGtmEvent('recovery_social_suggested', {
              provider: emailProvider.toLowerCase()
            });
            setControllerViews({
              socialData: {
                loginApple: emailProvider === SocialEnum.APPLE,
                loginFace: emailProvider === SocialEnum.FACEBOOK,
                loginGoogle: emailProvider === SocialEnum.GOOGLE
              }
            });
            nextStep();
          } else callCreateToken();
        }
      } catch {
        showErrorMessageServer();
        setLoading(false);
      }
    }
  }

  return (
    <div className="card__section">
      <div className="card__top">
        <StepHeader
          title="Esqueceu a senha?"
          description="Não tem problema. Informe o e-mail no qual deseja recuperar a senha."
        />

        <div className="form-group">
          <Alert errors={errors} />

          <FormField
            name="email"
            label="e-mail"
            type="email"
            placeholder="Seu e-mail"
            register={register('email')}
            error={errors}
            touchedFields={touchedFields}
            prefix
            iconPrefix="email"
          />
        </div>
      </div>

      <div className="button-group">
        <Button
          id="password-recovery"
          full
          onClick={emailExist}
          disabled={
            loading ||
            !!errors.email ||
            (fieldValidate?.length && fieldValidate?.length < 13) ||
            !fieldValidate?.length
          }
        >
          <ButtonLabel loading={loading}>continuar</ButtonLabel>
        </Button>

        <Button customColors="btn-white" full onClick={backPage}>
          <ButtonLabel>voltar</ButtonLabel>
        </Button>
      </div>
    </div>
  );
}
